import { getOperationName } from '@apollo/client/utilities';
import * as Sentry from '@sentry/react-native';
import { print } from 'graphql';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Image,
  Modal,
  PanResponder,
  Pressable,
  ScrollView,
  StyleSheet,
} from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import CachedImageNoLogin from '@components/CachedImage/CachedImageNoLogin';
import { Box, Text } from '@components/Restyle';
import {
  BOTTOM_H,
  chromeStyles,
  NAV_ICON,
  THUMB_H,
  THUMB_W,
  TOOLBAR_ICON,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
} from '@components/shared/mediaViewerChrome';
import { ToolbarTooltip } from '@components/shared/ToolbarTooltip.web';
import { File, SharedMediaByTokenDocument } from '@graphql/generated';
import Images from '@themes/images';
import theme from '@themes/theme';
import {
  fetchWithErrorHandling,
  HttpClientError,
} from '@utils/fetchWithErrorHandling';
import { getApiConfig } from '@utils/getApiConfig';
import { AsyncStorage } from '@utils/storage';

// Public `Copy link to share` image/video preview (route `/images-preview/:token`,
// rendered as a sibling overlay by auth-stack.web.tsx / projects-stack.web.tsx
// while logged out). TA-10293 unified this screen's chrome — header, stage,
// nav arrows, thumbnail strip — with the logged-in MediaViewer.web.tsx viewer
// (shared dimensions/colors live in mediaViewerChrome.ts) so a shared link
// no longer looks like a different product. No new capabilities were added:
// guests still only get download + zoom, matching the previous behavior.
export const ImagesPreviewNotLogin: React.FC<{
  onDismiss?: () => void;
}> = ({ onDismiss }) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [refetchData, setRefetchData] =
    useState<
      { clientId: string; file: { url: string; thumbnail: string } }[]
    >();
  const [loadError, setLoadError] = useState(false);
  const [mediaThumbnails, setMediaThumbnails] = useState<{
    [key: string]: string;
  }>({});
  // File names keyed by clientId, from the token payload (TA-10293) — used
  // to show a filename in the header like MediaViewer does. Legacy
  // AES-encrypted links carry no names, so this stays empty for them and
  // the header simply shows no filename.
  const [mediaNames, setMediaNames] = useState<{ [key: string]: string }>({});

  const shareToken = window.sessionStorage.getItem('share_token');

  const [images, setImages] = useState<string[]>(() => {
    if (shareToken) return [];
    return window.sessionStorage.getItem('images_path')?.split(',') ?? [];
  });

  const [sharer, setSharer] = useState<{
    name: string;
    avatar: File | null;
  }>(() => {
    if (shareToken) return { name: '', avatar: null };
    const userAvatar = window.sessionStorage.getItem('user_avatar') || '';
    const userName = window.sessionStorage.getItem('user_name') || '';
    return {
      name: userName,
      avatar: userAvatar
        ? {
            cdnBaseUrl: '',
            id: userAvatar,
            path: '',
            url: '',
            isPreviewable: true,
          }
        : null,
    };
  });

  // Resolve media + sharer info from the short /images-preview/:token link
  // in a single call (TA-10212), instead of the legacy AES-decrypted path.
  useEffect(() => {
    if (!shareToken || refetchData || loadError) return;

    const graphConfig = getApiConfig();
    fetch(`${graphConfig.apiUrl}/graphql`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Bypasses Apollo deliberately: this screen serves an unauthenticated
      // guest, and the client's global errorLink treats any 401 as a logout
      // signal — a bad side effect for a public share page. The query text
      // and operationName are still derived from the generated document
      // (not hand-typed) so they can't drift from the schema — the previous
      // hand-typed body omitted operationName, which the backend's
      // safe_single_operation_query? allowlist requires, causing every
      // guest link open to 401 (TA-10212 QA follow-up).
      body: JSON.stringify({
        // getOperationName types as string | null; fall back to the literal
        // so a null here can never silently regress into the 401 this PR
        // fixed (the backend allowlist requires an exact operationName match).
        operationName:
          getOperationName(SharedMediaByTokenDocument) ?? 'sharedMediaByToken',
        query: print(SharedMediaByTokenDocument),
        variables: { token: shareToken },
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          setLoadError(true);
          alert(t('models:files.downloadNetworkError'));
          return undefined;
        }
        return res.json();
      })
      .then((data) => {
        const payload = data?.data?.sharedMediaByToken;
        if (!payload) return;

        const docs = payload.documents ?? [];
        const names: { [key: string]: string } = {};
        setImages(
          docs.map((document: { clientId: string; name?: string }) => {
            const extension = document.name?.includes('.')
              ? document.name.split('.').pop()
              : '';
            if (document.name) names[document.clientId] = document.name;
            return extension
              ? `${document.clientId}.${extension}`
              : document.clientId;
          })
        );
        setMediaNames(names);
        setSharer({
          name: payload.sharerName ?? '',
          avatar: payload.sharerAvatar ?? null,
        });
        setRefetchData(docs);
      })
      .catch((error) => {
        Sentry.captureException(error);
        setLoadError(true);
        alert(t('models:files.downloadNetworkError'));
      });
  }, [shareToken, refetchData, loadError, t]);

  useEffect(() => {
    if (shareToken || clientIds.length === 0 || refetchData || loadError)
      return;

    const graphConfig = getApiConfig();
    fetch(`${graphConfig.apiUrl}/graphql`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: `
          query sharedDocumentsByClientIds(
            $clientIds: [ID!]
          ) {
            sharedDocumentsByClientIds(
              clientIds: $clientIds
            ) {
              clientId
              file {
                id
                url
                thumbnail
              }
            }
          }
        `,
        variables: {
          clientIds,
        },
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          let bodySnippet = '';
          try {
            const text = await res.text();
            bodySnippet = text.slice(0, 200);
          } catch {
            // ignore body read failures
          }
          Sentry.addBreadcrumb({
            category: 'http.graphql',
            message: `GraphQL ${res.status} — ${graphConfig.apiUrl}/graphql`,
            data: {
              url: `${graphConfig.apiUrl}/graphql`,
              status: res.status,
              body: bodySnippet,
            },
            level: 'error',
          });
          setLoadError(true);
          alert(t('models:files.downloadNetworkError'));
          return undefined;
        }
        return res.json();
      })
      .then((data) => {
        if (data) setRefetchData(data?.data?.sharedDocumentsByClientIds);
      })
      .catch((error) => {
        Sentry.captureException(error);
        setLoadError(true);
        alert(t('models:files.downloadNetworkError'));
      });
  }, [clientIds, refetchData, loadError]);

  useEffect(() => {
    if (refetchData) {
      AsyncStorage.multiSet(
        refetchData?.map((newItem: { clientId: any; file: { url: any } }) => [
          newItem.clientId,
          newItem.file.url,
        ])
      );
      const medias: { [key: string]: string } = {};
      refetchData?.map(
        (item: { clientId: string | number; file: { thumbnail: string } }) => {
          medias[item.clientId] = item.file.thumbnail;
        }
      );
      setMediaThumbnails(medias);
    }
  }, [refetchData]);

  const containsVideoExtension = (str: string) => {
    const videoExtensions = ['.mp4', '.mov', '.wmv', '.avi'];
    return videoExtensions.some((ext) => str?.toLowerCase().includes(ext));
  };

  const [zoom, setZoom] = useState(100);

  const navigateTo = (index: number) => {
    if (!images || index < 0 || index >= images.length) return;
    setZoom(100);
    setOffset({ x: 0, y: 0 });
    setActiveIndex(index);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.keyCode === 39) {
        navigateTo(activeIndex + 1);
      } else if (event.keyCode === 37) {
        navigateTo(activeIndex - 1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, images?.length]);

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderMove: (_evt, gestureState) => {
        setOffset({
          x: offset.x + gestureState.dx,
          y: offset.y + gestureState.dy,
        });
      },
      onPanResponderRelease: (_evt, gestureState) => {
        setOffset({
          x: offset.x + gestureState.dx,
          y: offset.y + gestureState.dy,
        });
      },
    })
  ).current;

  const [hoveredThumb, setHoveredThumb] = useState<number | null>(null);
  // Keep the active thumbnail in view, matching MediaViewer.web.tsx
  // (TA-10293) — thumbnails grew from 36px to THUMB_W (160px) as part of the
  // chrome unification, so without this a multi-image link's active thumb
  // scrolls out of view after a few arrow-key/arrow-button navigations.
  const thumbScrollRef = useRef<ScrollView>(null);
  const hasThumbStrip = images.length > 1;
  useEffect(() => {
    if (!hasThumbStrip) return;
    const id = setTimeout(() => {
      thumbScrollRef.current?.scrollTo({
        x: activeIndex * (THUMB_W + theme.spacing.xs),
        animated: true,
      });
    }, 50);
    return () => clearTimeout(id);
  }, [activeIndex, hasThumbStrip]);

  useEffect(() => {
    const clientIds: string[] = [];
    images?.map(async (item) => {
      clientIds.push(item.split('.')[0]);
    });
    setClientIds(clientIds);
  }, [images?.length]);

  const downloadImage = async () => {
    const clientId = images?.[activeIndex]?.split('.')[0];
    if (!clientId) return;
    const fileURL = await getUrl(clientId);

    fetchWithErrorHandling(fileURL, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    })
      .then((resp) => resp.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        if (!url) return;
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        // Prefer the real filename (e.g. "photo.jpg", surfaced in the header
        // — TA-10293) so the saved file keeps its extension; legacy AES links
        // carry no name, so fall back to the bare clientId as before.
        a.download =
          (clientId && mediaNames[clientId]) ||
          clientId ||
          new Date().getMilliseconds().toString();
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        const msg =
          err instanceof HttpClientError && err.status === 404
            ? t('models:files.downloadFileNotFound')
            : t('models:files.downloadNetworkError');
        alert(msg);
      });
  };

  const [videoUrls, setVideoUrls] = useState<{
    [key: string]: string;
  }>({});

  useEffect(() => {
    const getVideoUrls = async () => {
      const results: { [key: string]: string } = {};
      const urls: { [key: string]: string } = {};

      for (const item of images) {
        if (containsVideoExtension(item) && !results[item.split('.')[0]]) {
          let fullVideoUrl = await AsyncStorage.getItem(item.split('.')[0]);
          if (!fullVideoUrl) {
            fullVideoUrl = await getUrl(item.split('.')[0]);
          }
          urls[item.split('.')[0]] = fullVideoUrl;
        }
      }
      setVideoUrls(urls);
    };

    if (images && images.length > 0) {
      getVideoUrls();
    }
  }, [images?.length, refetchData]);

  const getUrl = async (clientId?: string) => {
    let fileURL = '';
    if (clientId) {
      fileURL = (await AsyncStorage.getItem(clientId)) ?? '';
    }

    if (!fileURL && clientId) {
      setTimeout(getUrl, 1000);
    }
    return fileURL;
  };

  // ── Current item derived state ────────────────────────────────────────────
  const activeItem = images?.[activeIndex];
  const activeClientId = activeItem?.split('.')[0];
  const isActiveVideo = !!activeItem && containsVideoExtension(activeItem);
  const isActiveImage = !!activeItem && !isActiveVideo;
  const filename = activeClientId ? (mediaNames[activeClientId] ?? '') : '';

  const [imageAreaWidth, setImageAreaWidth] = useState<number | undefined>();
  const [imageAreaHeight, setImageAreaHeight] = useState<number | undefined>();

  const renderMediaContent = () => {
    if (!activeItem) return null;
    if (isActiveVideo) {
      const videoUrl = videoUrls[activeClientId as string];
      const poster = mediaThumbnails[activeClientId as string];
      if (!videoUrl) {
        // While the URL resolves (getVideoUrls polls AsyncStorage, then
        // falls back to getUrl — see below), show the poster instead of a
        // blank stage; there's no bounded retry here that would justify a
        // loading spinner over just the thumbnail (TA-10293).
        return poster ? (
          <Image
            source={{ uri: poster }}
            style={{
              width: imageAreaWidth ?? '100%',
              height: imageAreaHeight ?? '100%',
            }}
            resizeMode='contain'
          />
        ) : null;
      }
      return (
        <video
          key={activeItem}
          controls
          poster={poster}
          style={{
            width: imageAreaWidth ?? '100%',
            height: imageAreaHeight ?? '100%',
            objectFit: 'contain',
          }}
          src={videoUrl}
          onError={(e) => console.error('Error loading video', e)}
        />
      );
    }
    return (
      <Box key={activeItem} {...panResponder.panHandlers}>
        <CachedImageNoLogin
          imageClientId={activeClientId}
          image={activeClientId as string}
          screenWidth={imageAreaWidth}
          height={imageAreaHeight}
          style={{
            // Matches MediaViewer's dark stage instead of
            // CachedImageNoLogin's light-grey default (TA-10293) — the
            // letterbox bars around a contained image are this color.
            backgroundColor: theme.colors.viewerImageBg,
            // Order matters: translate must be the outer transform (applied
            // before scale) so pan offset is in screen pixels, not scaled by
            // zoom — matches MediaViewer.web.tsx's transform order (TA-10293).
            transform: [
              { translateX: zoom > 100 ? offset.x : 0 },
              { translateY: zoom > 100 ? offset.y : 0 },
              { scale: zoom / 100 },
            ],
          }}
          resizeMode='contain'
        />
      </Box>
    );
  };

  return (
    // No onRequestClose: on web that wires the Escape key to onDismiss, which
    // drops a logged-out guest onto the auth stack with no route back to the
    // shared link except a page reload (TA-10293) — the previous WebModal
    // wrapper never wired Escape either.
    <Modal visible transparent animationType='fade'>
      <Box style={s.root}>
        {/* ── HEADER ──────────────────────────────────────────────────────── */}
        <Box style={s.header}>
          <Box style={s.hLeft}>
            <Pressable
              onPress={onDismiss}
              style={s.iconBtn}
              hitSlop={10}
              accessibilityLabel={t('models:chat.imageViewer.closeViewer')}>
              <Images.ChevronLeft
                width={24}
                height={24}
                color={theme.colors.white}
              />
            </Pressable>
            <Box ml='xxs' flexShrink={0}>
              <Avatar
                avatar={sharer.avatar}
                id={sharer.avatar?.id}
                label={sharer.name}
                size='small'
              />
            </Box>
            <Box style={s.senderBlock}>
              <Text style={s.senderName} numberOfLines={1}>
                {sharer.name}
              </Text>
              <Text style={s.dateLabel} numberOfLines={1}>
                {t('models:projects.files.shareImagesWithYou')}
              </Text>
            </Box>
          </Box>

          <Box style={s.hCenter}>
            <Box style={s.filenameRow}>
              <Text style={s.filenameStatic} numberOfLines={1}>
                {filename}
              </Text>
            </Box>
          </Box>

          <Box style={s.hRight}>
            <Box style={s.iconGroup}>
              <ToolbarTooltip label={t('models:chat.imageViewer.download')}>
                <Pressable
                  onPress={downloadImage}
                  style={({ pressed, hovered }: any) => [
                    s.iconBtn,
                    (pressed || hovered) && s.iconBtnHover,
                  ]}
                  hitSlop={10}
                  accessibilityLabel={t('models:chat.imageViewer.download')}>
                  <Images.Download
                    width={TOOLBAR_ICON}
                    height={TOOLBAR_ICON}
                    color={theme.colors.white}
                  />
                </Pressable>
              </ToolbarTooltip>
            </Box>

            {isActiveImage && (
              <>
                <Box style={s.groupDivider} />
                <Box style={s.iconGroup}>
                  <ToolbarTooltip label={t('models:chat.imageViewer.zoomOut')}>
                    <Pressable
                      onPress={() => {
                        setZoom((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN));
                        setOffset({ x: 0, y: 0 });
                      }}
                      style={({ pressed, hovered }: any) => [
                        s.iconBtn,
                        (pressed || hovered) && s.iconBtnHover,
                      ]}
                      hitSlop={10}
                      accessibilityLabel={t('models:chat.imageViewer.zoomOut')}>
                      <Images.ZoomOut
                        width={TOOLBAR_ICON}
                        height={TOOLBAR_ICON}
                        color={
                          zoom <= ZOOM_MIN
                            ? theme.colors.scrimLight30
                            : theme.colors.white
                        }
                      />
                    </Pressable>
                  </ToolbarTooltip>
                  <Text style={s.zoomPct} testID='zoom-pct'>
                    {zoom}%
                  </Text>
                  <ToolbarTooltip label={t('models:chat.imageViewer.zoomIn')}>
                    <Pressable
                      onPress={() => {
                        setZoom((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX));
                        setOffset({ x: 0, y: 0 });
                      }}
                      style={({ pressed, hovered }: any) => [
                        s.iconBtn,
                        (pressed || hovered) && s.iconBtnHover,
                      ]}
                      hitSlop={10}
                      accessibilityLabel={t('models:chat.imageViewer.zoomIn')}>
                      <Images.ZoomIn
                        width={TOOLBAR_ICON}
                        height={TOOLBAR_ICON}
                        color={
                          zoom >= ZOOM_MAX
                            ? theme.colors.scrimLight30
                            : theme.colors.white
                        }
                      />
                    </Pressable>
                  </ToolbarTooltip>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* ── IMAGE AREA ──────────────────────────────────────────────────── */}
        <Box
          testID='images-preview-image-area'
          style={s.imageArea}
          onLayout={(e) => {
            setImageAreaWidth(e.nativeEvent.layout.width);
            setImageAreaHeight(e.nativeEvent.layout.height);
          }}>
          {renderMediaContent()}

          {images.length > 1 && (
            <>
              {activeIndex !== 0 && (
                <Pressable
                  onPress={() => navigateTo(activeIndex - 1)}
                  style={[s.navBtn, s.navLeft]}
                  hitSlop={16}
                  accessibilityLabel={t(
                    'models:chat.imageViewer.previousImage'
                  )}>
                  <Images.ArrowLeft
                    width={NAV_ICON}
                    height={NAV_ICON}
                    color={theme.colors.white}
                  />
                </Pressable>
              )}
              {activeIndex !== images.length - 1 && (
                <Pressable
                  onPress={() => navigateTo(activeIndex + 1)}
                  style={[s.navBtn, s.navRight]}
                  hitSlop={16}
                  accessibilityLabel={t('models:chat.imageViewer.nextImage')}>
                  <Images.ArrowRight
                    width={NAV_ICON}
                    height={NAV_ICON}
                    color={theme.colors.white}
                  />
                </Pressable>
              )}
            </>
          )}
        </Box>

        {/* ── BOTTOM THUMBNAIL STRIP ──────────────────────────────────────── */}
        {images.length > 1 && (
          <Box style={s.bottomBar}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={s.thumbStrip}
              ref={thumbScrollRef}
              testID='images-preview-thumb-strip'
              style={{ height: BOTTOM_H }}>
              {images.map((item, index) => {
                const isVideo = containsVideoExtension(item);
                const clientId = item.split('.')[0];
                return (
                  <Pressable
                    key={item}
                    onPress={() => navigateTo(index)}
                    style={{ position: 'relative' }}
                    {...({
                      onMouseEnter: () => setHoveredThumb(index),
                      onMouseLeave: () => setHoveredThumb(null),
                    } as any)}
                    accessibilityLabel={
                      activeIndex === index
                        ? `Currently viewing ${isVideo ? 'video' : 'image'} ${
                            index + 1
                          }`
                        : `View ${isVideo ? 'video' : 'image'} ${index + 1}`
                    }>
                    <Box
                      style={[
                        s.thumbBorder,
                        activeIndex === index && s.thumbActive,
                      ]}>
                      <Box style={s.thumbWrap}>
                        {isVideo ? (
                          <Image
                            source={{ uri: mediaThumbnails[clientId] }}
                            style={s.thumb}
                            resizeMode='cover'
                          />
                        ) : (
                          <CachedImageNoLogin
                            imageClientId={clientId}
                            image={mediaThumbnails[clientId]}
                            isThumbnail
                            width={THUMB_W}
                            height={THUMB_H}
                            style={{
                              backgroundColor: theme.colors.scrimLight06,
                            }}
                          />
                        )}
                        {hoveredThumb === index && (
                          <Box style={s.thumbHoverOverlay} />
                        )}
                      </Box>
                    </Box>
                  </Pressable>
                );
              })}
            </ScrollView>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

const s = StyleSheet.create({
  ...chromeStyles,
  // MediaViewer's `filename` is dashed-underlined to signal "click to
  // rename" — guests can't rename, so this copies chromeStyles.filename
  // without the underline instead of reusing that key (TA-10293).
  filenameStatic: {
    color: theme.colors.white,
    fontSize: 13,
    fontWeight: '500',
    flexShrink: 1,
  },
});
